/**
 * Theme Localization System
 *
 * Maps concept names and UI strings between themes.
 * Modern theme: Professional/technical terminology
 * Command theme: Sci-fi/military terminology
 */

export interface ThemeLocale {
  // Core concepts
  stream: string;
  streams: string;
  task: string;
  tasks: string;
  agent: string;
  agents: string;

  // Views/Pages
  dashboard: string;
  workMode: string;

  // Status/States
  attention: string;
  attentionItems: string;
  running: string;
  completed: string;
  pending: string;
  failed: string;
  sleeping: string;

  // Actions
  newStream: string;
  createStream: string;
  archiveStream: string;
  deleteStream: string;

  // Agent/Droid related
  agentPanel: string;
  agentLevel: string;
  streamLevel: string;
  taskLevel: string;

  // Context
  contextStore: string;
  sharedContext: string;

  // UI Labels
  search: string;
  searchPlaceholder: string;
  settings: string;
  integrations: string;

  // Status labels
  statusRunning: string;
  statusCompleted: string;
  statusPending: string;
  statusFailed: string;
  statusSleeping: string;
  statusAttention: string;

  // Empty states
  noStreams: string;
  noTasks: string;
  noAttentionItems: string;

  // Time
  timeFormat: 'standard' | 'military';

  // Misc UI
  approve: string;
  reject: string;
  retry: string;
  cancel: string;
  save: string;
  close: string;

  // Visibility
  visibilityPrivate: string;
  visibilityTeam: string;
  visibilityOrg: string;
  visibilityRestricted: string;
  manageAccess: string;
}

export const modernLocale: ThemeLocale = {
  // Core concepts
  stream: 'Stream',
  streams: 'Streams',
  task: 'Task',
  tasks: 'Tasks',
  agent: 'Agent',
  agents: 'Agents',

  // Views/Pages
  dashboard: 'Dashboard',
  workMode: 'Work Mode',

  // Status/States
  attention: 'Attention',
  attentionItems: 'Attention Items',
  running: 'Running',
  completed: 'Completed',
  pending: 'Pending',
  failed: 'Failed',
  sleeping: 'Sleeping',

  // Actions
  newStream: 'New Stream',
  createStream: 'Create Stream',
  archiveStream: 'Archive Stream',
  deleteStream: 'Delete Stream',

  // Agent related
  agentPanel: 'Agent Panel',
  agentLevel: 'Agent Level',
  streamLevel: 'Stream Level',
  taskLevel: 'Task Level',

  // Context
  contextStore: 'Context Store',
  sharedContext: 'Shared Context',

  // UI Labels
  search: 'Search',
  searchPlaceholder: 'Search streams, tasks...',
  settings: 'Settings',
  integrations: 'Integrations',

  // Status labels
  statusRunning: 'Running',
  statusCompleted: 'Completed',
  statusPending: 'Pending',
  statusFailed: 'Failed',
  statusSleeping: 'Sleeping',
  statusAttention: 'Needs Attention',

  // Empty states
  noStreams: 'No streams yet',
  noTasks: 'No tasks in this stream',
  noAttentionItems: 'All clear! No items need attention.',

  // Time
  timeFormat: 'standard',

  // Misc UI
  approve: 'Approve',
  reject: 'Reject',
  retry: 'Retry',
  cancel: 'Cancel',
  save: 'Save',
  close: 'Close',

  // Visibility
  visibilityPrivate: 'Private',
  visibilityTeam: 'Team',
  visibilityOrg: 'Organization',
  visibilityRestricted: 'Restricted',
  manageAccess: 'Manage Access',
};

export const commandLocale: ThemeLocale = {
  // Core concepts
  stream: 'Mission',
  streams: 'Missions',
  task: 'Directive',
  tasks: 'Directives',
  agent: 'Droid',
  agents: 'Droids',

  // Views/Pages
  dashboard: 'Command Center',
  workMode: 'Tactical View',

  // Status/States
  attention: 'Alert',
  attentionItems: 'Incoming Alerts',
  running: 'Executing',
  completed: 'Accomplished',
  pending: 'Queued',
  failed: 'Malfunction',
  sleeping: 'Standby',

  // Actions
  newStream: 'New Mission',
  createStream: 'Deploy Mission',
  archiveStream: 'Archive Mission',
  deleteStream: 'Terminate Mission',

  // Agent related
  agentPanel: 'Droid Interface',
  agentLevel: 'Droid Level',
  streamLevel: 'Mission Level',
  taskLevel: 'Directive Level',

  // Context
  contextStore: 'Intel Database',
  sharedContext: 'Shared Intel',

  // UI Labels
  search: 'Scan',
  searchPlaceholder: 'Scan missions, directives...',
  settings: 'Configuration',
  integrations: 'External Systems',

  // Status labels
  statusRunning: 'Executing',
  statusCompleted: 'Accomplished',
  statusPending: 'Queued',
  statusFailed: 'Malfunction',
  statusSleeping: 'Standby',
  statusAttention: 'Requires Input',

  // Empty states
  noStreams: 'No active missions',
  noTasks: 'No directives assigned',
  noAttentionItems: 'All systems nominal. No alerts.',

  // Time
  timeFormat: 'military',

  // Misc UI
  approve: 'Authorize',
  reject: 'Deny',
  retry: 'Reinitiate',
  cancel: 'Abort',
  save: 'Confirm',
  close: 'Dismiss',

  // Visibility
  visibilityPrivate: 'Classified',
  visibilityTeam: 'Squadron',
  visibilityOrg: 'Fleet',
  visibilityRestricted: 'Need-to-Know',
  manageAccess: 'Access Control',
};

export const locales = {
  modern: modernLocale,
  command: commandLocale,
} as const;

export type ThemeName = keyof typeof locales;

export function getLocale(theme: ThemeName): ThemeLocale {
  return locales[theme];
}
